/* ==========================================================================
   OTM — secondary PDP price synchronisation
   --------------------------------------------------------------------------
   Ethereal refreshes price surfaces on variant change by element ID, in
   ProductInfo.handleUpdateProductInfo (assets/t-product.js):

       const source      = html.getElementById(`${id}-${this.sectionId}`);
       const destination = this.querySelector(`#${id}-${this.dataset.section}`);
       ...
       updateSourceFromDestination('price');

   Only the `price` block carries such an ID (#price-<section.id>, set in
   snippets/pdp-actions.liquid). Two other price surfaces inside <product-info>
   carry no ID and are therefore never re-synced:

     1. The mobile price beside the title  — snippets/pdp-info.liquid,
        <x-cell class="small-only mobile-price text-end">, shown below 777px
        when the section setting `priceOnMobile` is on.
     2. The quick-purchase tab price       — sections/t-product.liquid,
        <preview-tab> ... <span class="price">, shown below 777px when the
        section setting `previewTab` is on. That one additionally renders
        `product.price` (the product MINIMUM) rather than the selected
        variant, so it is wrong even on a direct ?variant= load.

   Both kept the price of whichever variant was rendered on page load, while
   the main price, URL, cart and checkout followed the selection correctly.

   This module mirrors the main price block into those two surfaces whenever
   the theme updates it. It observes the DOM rather than hooking the theme's
   internals, so it depends on no script load order and no private API, and it
   keeps assets/t-product.js and sections/t-product.liquid unforked.

   All values are taken from the server-rendered price markup, so currency
   formatting, compare-at/sale presentation and the visually-hidden price
   labels are whatever Liquid produced — nothing is computed or formatted
   here, and no price is ever hard-coded.
   ========================================================================== */

(function () {
  'use strict';

  var ROOTS = 'product-info[data-section]';
  var MAIN = 'section.price';          /* #price-<section.id> in pdp-actions.liquid */
  var PRICE = 'coretex-price';         /* wrapper emitted by snippets/c-price.liquid */
  var MOBILE = '.mobile-price';        /* x-cell in pdp-info.liquid                 */
  var TAB = 'preview-tab .price';      /* span in t-product.liquid                  */

  function sync(root) {
    var main = root.querySelector(MAIN);
    if (!main) return;

    var source = main.querySelector(PRICE);
    if (!source) return;

    var hidden = main.classList.contains('hidden');

    /* 1. Mobile price — same c-price markup, so copy it wholesale. This
          carries the sale/regular structure and the visually-hidden labels,
          leaving no stale accessibility text behind. */
    var cell = root.querySelector(MOBILE);
    if (cell) {
      var mobile = cell.querySelector(PRICE);
      if (mobile) {
        if (mobile.innerHTML !== source.innerHTML) mobile.innerHTML = source.innerHTML;
        if (mobile.className !== source.className) mobile.className = source.className;
      }
      cell.classList.toggle('hidden', hidden);
    }

    /* 2. Quick-purchase tab — a single plain amount, so take the active
          price only: the sale price when the variant is on sale, otherwise
          the regular one. Text is copied, never reformatted. */
    var tab = root.querySelector(TAB);
    if (tab) {
      var active = source.querySelector('.price-item.sale') || source.querySelector('.price-item.regular');
      if (active) {
        var text = active.textContent.trim();
        if (text && tab.textContent.trim() !== text) tab.textContent = text;
      }
      tab.classList.toggle('hidden', hidden);
    }
  }

  function observe(root) {
    if (root.hasAttribute('data-otm-price-sync')) return;
    root.setAttribute('data-otm-price-sync', '');

    /* Runs once up front too: this is what corrects the quick-purchase tab on
       a direct ?variant= load, before any option has been changed. */
    sync(root);

    var main = root.querySelector(MAIN);
    if (!main || typeof window.MutationObserver !== 'function') return;

    new window.MutationObserver(function () {
      sync(root);
    }).observe(main, {
      childList: true,      /* updateSourceFromDestination replaces innerHTML */
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class'] /* setUnavailable() toggles .hidden */
    });
  }

  function boot() {
    var roots = document.querySelectorAll(ROOTS);
    for (var i = 0; i < roots.length; i++) observe(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
